import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { USOfficeContact } from '../components/contact/USOfficeContact';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Send contact form data to backend
      await axios.post(`${API}/contact`, {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        company: formData.company,
        message: `Job Title: ${formData.jobTitle}\n\n${formData.message}`
      });
      
      setFormSubmitted(true);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: '',
        message: '',
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Let's Start the Conversation
              </h1>
              <p className="text-xl text-gray-300">
                You're one step closer to smarter performance management. Whether you're ready to transform your organization's strategy or just want to learn more—we're here for it.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Let's Connect!</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Have a question about us or need help with your organizational performance? We are here to help.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email Us</h3>
                      <a href="mailto:karna.vijay@gmail.com" className="text-emerald-600 hover:text-emerald-700">
                        karna.vijay@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Call Us</h3>
                      <a href="tel:+919963171105" className="text-emerald-600 hover:text-emerald-700">
                        +91-9963171105
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Visit Us</h3>
                      <p className="text-gray-600">Hitech City</p>
                      <p className="text-gray-600">Hyderabad, India</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Business Hours</h3>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                      <p className="text-gray-600">Saturday - Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                <USOfficeContact />

                {/* Image */}
                <div className="mt-12">
                  <img
                    src="https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=500"
                    alt="Team Meeting"
                    className="rounded-2xl shadow-lg w-full"
                  />
                </div>
              </div>

              {/* Form */}
              <div className="bg-gray-50 rounded-2xl p-8">
                {formSubmitted ? (
                  <div data-testid="contact-form-success" role="status" className="h-full flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                    <p className="text-gray-600">We've received your message and will get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-first-name" className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <Input
                          id="contact-first-name"
                          data-testid="contact-first-name-input"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-last-name" className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <Input
                          id="contact-last-name"
                          data-testid="contact-last-name-input"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">Work Email *</label>
                      <Input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                      <Input
                        id="contact-company"
                        data-testid="contact-company-input"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-job-title" className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                      <select
                        id="contact-job-title"
                        data-testid="contact-job-title-select"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        required
                        className="h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        <option data-testid="contact-job-title-placeholder" value="" disabled>Select your role</option>
                        <option data-testid="contact-job-title-ceo" value="Chief Executive Officer">Chief Executive Officer</option>
                        <option data-testid="contact-job-title-cfo" value="Chief Financial Officer">Chief Financial Officer</option>
                        <option data-testid="contact-job-title-coo" value="Chief Operating Officer">Chief Operating Officer</option>
                        <option data-testid="contact-job-title-cto" value="Chief Technology Officer">Chief Technology Officer</option>
                        <option data-testid="contact-job-title-cmo" value="Chief Marketing Officer">Chief Marketing Officer</option>
                        <option data-testid="contact-job-title-vp" value="VP / Director">VP / Director</option>
                        <option data-testid="contact-job-title-manager" value="Manager">Manager</option>
                        <option data-testid="contact-job-title-other" value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <Textarea
                        id="contact-message"
                        data-testid="contact-message-input"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your needs..."
                        rows={4}
                      />
                    </div>

                    {error && (
                      <div data-testid="contact-form-error" role="alert" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <Button 
                      data-testid="contact-form-submit-button"
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700" 
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting this form, you agree to our Privacy Policy and Terms of Service.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
